import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PlaylistVideo } from '@/store/playlist/types';

interface SortableVideoItemProps {
  video: PlaylistVideo;
  videoData: {
    name: string;
    thumbnail: string;
    duration: number;
  };
  index: number;
  onRemove: (id: string) => void;
}

export function SortableVideoItem({
  video,
  videoData,
  index,
  onRemove,
}: SortableVideoItemProps) {
  useLanguage();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: video.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-3 bg-card border border-border rounded-xl p-3 transition-all ${
        isDragging ? 'shadow-lg opacity-50 scale-[1.02]' : 'hover:border-[#7A5CFF]/50'
      }`}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors touch-none"
      >
        <GripVertical className="h-5 w-5" />
      </button>

      {/* Order Number */}
      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#F0ECFF] text-[#7A5CFF] font-semibold text-sm shrink-0">
        {index + 1}
      </div>

      {/* Thumbnail */}
      <div className="relative flex-shrink-0 w-24 h-14 sm:w-32 sm:h-18 overflow-hidden rounded-lg group/thumb">
        <img
          src={videoData.thumbnail}
          alt={videoData.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-opacity">
          <Play className="h-5 w-5 text-white" />
        </div>
        <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/80 rounded text-[10px] text-white font-medium">
          {formatDuration(videoData.duration)}
        </div>
      </div>

      {/* Video Info */}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-sm line-clamp-2">{videoData.name}</h4>
        <p className="text-xs text-muted-foreground mt-1">
          {translateInterfaceText("Episódio")}{" "}{index + 1}
        </p>
      </div>

      {/* Remove Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onRemove(video.id)}
        className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/10 hover:text-destructive"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
